import {AuthModel, UserModel} from './_models'
import {deriveMenuFromToken, isTokenExpired as checkTokenExpired, getUserEmailFromToken} from './PermissionHelper'
import Cookies from 'js-cookie'

// Cookie configuration
const AUTH_TOKEN_COOKIE = 'auth_token'
const USER_LOCAL_STORAGE_KEY = 'kt-user-react-v'

// Cookie options for maximum security (frontend-side)
const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  secure: true, // Only transmitted over HTTPS
  sameSite: 'Strict', // CSRF protection - cookie only sent to same site
  expires: 7, // Auto-expire after 7 days
  path: '/', // Available to entire application
}

/**
 * SECURITY: Retrieves auth from secure cookie only
 * - Token: Stored in secure cookie with Secure, SameSite=Strict flags
 * - Menu: Derived from JWT token (cryptographically signed permissions)
 * - Provider: NOT stored here - populated in-memory by React Query in Auth.tsx
 *
 * Note: This returns basic auth (token + menu). Provider data added later by Auth.tsx
 */
const getAuth = (): AuthModel | undefined => {
  try {
    // MIGRATION: Clean up old/deprecated localStorage keys (one-time cleanup)
    // This ensures users who upgrade to new version have old data removed
    if (localStorage) {
      if (localStorage.getItem('kt-auth-metadata') !== null) {
        localStorage.removeItem('kt-auth-metadata')
      }
      if (localStorage.getItem('kt-auth-react-v') !== null) {
        // Only remove if we have token in cookie (successful migration)
        if (Cookies.get(AUTH_TOKEN_COOKIE)) {
          localStorage.removeItem('kt-auth-react-v')
        }
      }
      // Clean up user storage (now using React Query only)
      if (localStorage.getItem(USER_LOCAL_STORAGE_KEY) !== null) {
        if (Cookies.get(AUTH_TOKEN_COOKIE)) {
          localStorage.removeItem(USER_LOCAL_STORAGE_KEY)
        }
      }
    }

    // Get token from secure cookie
    const token = Cookies.get(AUTH_TOKEN_COOKIE)
    if (!token) {
      return undefined
    }

    // CRITICAL SECURITY: Always derive menu from JWT token (cryptographically signed)
    const derivedMenu = deriveMenuFromToken(token)

    // Extract userEmail from JWT token for convenience
    const userEmail = getUserEmailFromToken(token)

    return {
      token,
      menu: derivedMenu,
      userEmail,
      // Provider will be added in-memory by Auth.tsx from API
    } as AuthModel
  } catch (error) {
    console.error('AUTH RETRIEVAL ERROR', error)
    return undefined
  }
}

/**
 * SECURITY: Stores ONLY token in secure cookie
 * - Token: Secure cookie with HTTPS, SameSite=Strict, auto-expiration
 * - Menu: NEVER stored - always derived from JWT
 * - Provider: Can be in auth object (in-memory), but NOT stored in cookie
 *
 * Note: Auth object may contain provider data in-memory (from API),
 * but only token is persisted to cookie. Provider data is ephemeral.
 */
const setAuth = (auth: AuthModel) => {
  try {
    // Store ONLY token in secure cookie (provider data not stored)
    Cookies.set(AUTH_TOKEN_COOKIE, auth.token, COOKIE_OPTIONS)

    // Menu is derived from JWT token on every getAuth() call
    // Provider info may be in auth object (in-memory) but not persisted
  } catch (error) {
    console.error('AUTH STORAGE ERROR', error)
  }
}

/**
 * SECURITY: Removes token cookie and cleans up all localStorage keys
 */
const removeAuth = () => {
  try {
    // Remove token cookie
    Cookies.remove(AUTH_TOKEN_COOKIE, {path: '/'})

    // Clean up ALL localStorage keys (no user data stored anymore)
    if (localStorage) {
      localStorage.removeItem('kt-auth-metadata') // Old metadata storage (removed in update)
      localStorage.removeItem('kt-auth-react-v') // Old full auth storage (before cookies)
      localStorage.removeItem(USER_LOCAL_STORAGE_KEY) // User data (now using React Query only)
    }
  } catch (error) {
    console.error('AUTH REMOVAL ERROR', error)
  }
}

const setUser = (user: UserModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(user)
    localStorage.setItem(USER_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('USER LOCAL STORAGE SAVE ERROR', error)
  }
}

const getUser = (): UserModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(USER_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const user: UserModel = JSON.parse(lsValue) as UserModel
    if (user) {
      return user
    }
  } catch (error) {
    console.error('USER LOCAL STORAGE PARSE ERROR', error)
  }
}

const removeUser = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(USER_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('USER LOCAL STORAGE REMOVE ERROR', error)
  }
}

// Utility function to handle token expiration (for API interceptors)
const handleTokenExpiration = () => {
  removeAuth()
  removeUser()

  // Redirect to login page or reload to trigger login flow
  // window.location.href = '/auth/login'
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'

  // Request interceptor - check token expiration before making request
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getAuth()
      if (auth && auth.token) {
        // Check if token is expired before making request
        if (checkTokenExpired(auth.token)) {
          handleTokenExpiration()
          return Promise.reject(new Error('Token expired'))
        }

        config.headers.Authorization = `Bearer ${auth.token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  // Response interceptor - handle 401 responses (token expired/invalid)
  axios.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (error.response && error.response.status === 401) {
        handleTokenExpiration()
      }
      return Promise.reject(error)
    }
  )
}

// Export legacy key for backward compatibility (if needed elsewhere)
export const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v' // Deprecated - now using cookies
export {getAuth, setAuth, removeAuth, setUser, getUser, removeUser}
